import React, { useEffect, useState } from "react";
import { sitePath } from "../../lib/siteRouting";
import { Link, useNavigate } from "react-router-dom";
import publicApi from "../../api/public";
import SiteLayout from "../../components/site/SiteLayout";
import {
  ArrowRight, Building2, ShoppingBag, Wrench, Search,
  MapPin, Home as HomeIcon, ChevronLeft, ChevronRight,
} from "lucide-react";
import SEOHead from "../../components/site/SEOHead";
import { SmartImage } from "../../components/site/SmartImage";
import { motion, AnimatePresence } from "framer-motion";
import StatsBand from "../../components/site/StatsBand";
import WhyTpco from "../../components/site/WhyTpco";
import MethodTimeline from "../../components/site/MethodTimeline";
import Testimonials from "../../components/site/Testimonials";
import NewsletterCta from "../../components/site/NewsletterCta";
import MortgageCalculator from "../../components/site/MortgageCalculator";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Stock images haut de gamme (Unsplash) — architecture sénégalaise / immeubles modernes
const HERO_SLIDES = [
  {
    image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&w=1920&q=80",
    title: "Je choisis",
    highlight: "TPCO",
    sub: "ma maison · mon investissement",
  },
  {
    image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1920&q=80",
    title: "Je construis",
    highlight: "mon avenir",
    sub: "résidences neuves clé en main à Dakar",
  },
  {
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80",
    title: "J'investis",
    highlight: "au Sénégal",
    sub: "programmes neufs · vente · location-vente",
  },
];

const CTAS = [
  { icon: Building2, label: "Projets", sub: "Résidences neuves", to: sitePath("/projets"), color: "from-[#1E3A8A] to-[#0F1A3D]" },
  { icon: ShoppingBag, label: "Catalogue", sub: "Biens à vendre", to: "https://vente.tpco.sn", color: "from-[#E91E63] to-[#9C1141]", external: true },
  { icon: Wrench, label: "Sur-mesure", sub: "Devis personnalisé", to: sitePath("/contact"), color: "from-[#2D7A3F] to-[#1A4A26]" },
];

export default function SiteHome() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [slide, setSlide] = useState(0);
  // Recherche hero
  const [searchType, setSearchType] = useState("");
  const [searchVille, setSearchVille] = useState("");
  const [searchBudget, setSearchBudget] = useState("");

  useEffect(() => {
    publicApi.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  // Auto-advance carousel
  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  const goSearch = (e) => {
    e?.preventDefault();
    const params = new URLSearchParams();
    if (searchType) params.set("type_unite", searchType);
    if (searchVille) params.set("ville", searchVille);
    if (searchBudget) params.set("prix_max", searchBudget);
    const url = `https://vente.tpco.sn/?${params.toString()}`;
    window.location.href = url;
  };

  const current = HERO_SLIDES[slide];

  return (
    <SiteLayout>
      <SEOHead
        title="Construction et immobilier à Dakar"
        description="TPCO Sénégal — Construction de bâtiments, promotion immobilière, gestion locative à Dakar. Découvrez nos projets, calculez votre mensualité et téléchargez notre brochure."
      />

      {/* ===== HERO PLEIN ÉCRAN ===== */}
      <section className="relative h-[88vh] min-h-[640px] overflow-hidden" data-testid="hero">
        {/* Slides */}
        <AnimatePresence mode="sync">
          <motion.div
            key={slide}
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            <img src={current.image} alt={current.highlight} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F1A3D]/85 via-[#0F1A3D]/55 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A3D]/70 via-transparent to-transparent" />
          </motion.div>
        </AnimatePresence>

        {/* Slide indicators */}
        <div className="absolute top-6 right-6 z-20 flex items-center gap-2">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-1 transition-all ${i === slide ? "w-10 bg-[#E91E63]" : "w-5 bg-white/40 hover:bg-white/60"}`}
              data-testid={`hero-dot-${i}`}
            />
          ))}
        </div>

        {/* Content */}
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-center text-white">
          <AnimatePresence mode="wait">
            <motion.div
              key={slide}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl"
            >
              <div className="text-[12px] uppercase tracking-[0.35em] text-white/70 font-bold mb-4">// TPCO Sénégal</div>
              <h1 className="font-display text-5xl md:text-6xl lg:text-[88px] font-extrabold leading-[0.95] tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                {current.title} <span className="text-[#E91E63]">{current.highlight}</span>.
              </h1>
              <p className="text-base md:text-lg text-white/80 mt-5 lg:mt-7 max-w-xl leading-relaxed">
                {current.sub}
              </p>
            </motion.div>
          </AnimatePresence>

          {/* Search bar intégré au hero */}
          <motion.form
            onSubmit={goSearch}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 lg:mt-14 max-w-4xl bg-white rounded-xl p-3 lg:p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-3 shadow-2xl"
            data-testid="hero-search"
          >
            <div className="col-span-1">
              <label className="text-[9px] uppercase tracking-wider text-[#4B5775] font-bold block mb-1">Type de bien</label>
              <select value={searchType} onChange={(e) => setSearchType(e.target.value)} className="w-full h-11 px-3 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] text-[#0F1A3D] bg-white" data-testid="hero-search-type">
                <option value="">Tous types</option>
                <option value="appartement">Appartement</option>
                <option value="maison">Maison</option>
                <option value="studio">Studio</option>
                <option value="duplex">Duplex</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-wider text-[#4B5775] font-bold block mb-1">Ville</label>
              <div className="relative">
                <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3B5]" />
                <input value={searchVille} onChange={(e) => setSearchVille(e.target.value)} placeholder="Dakar, Almadies…" className="w-full h-11 pl-9 pr-3 text-sm rounded-lg border border-[#E2E5F1] focus:outline-none focus:border-[#1E3A8A] text-[#0F1A3D]" data-testid="hero-search-ville" />
              </div>
            </div>
            <div className="flex items-end">
              <button type="submit" className="w-full h-11 bg-[#E91E63] hover:bg-[#D81B60] text-white text-[12px] font-bold uppercase tracking-wider rounded-lg flex items-center justify-center gap-2 transition" data-testid="hero-search-btn">
                <Search className="w-4 h-4" /> Rechercher
              </button>
            </div>
          </motion.form>
        </div>
      </section>

      {/* ===== 3 CTA cards : section dédiée APRÈS le hero (plus robuste qu'un overlap absolu) ===== */}
      <section style={{ background: "#F8FAFC", padding: "48px 0" }} data-testid="hero-ctas-section">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5">
            {CTAS.map((c, i) => {
              const Icon = c.icon;
              const inner = (
                <div
                  style={{
                    background: "#FFFFFF",
                    border: "1px solid #E2E5F1",
                    borderRadius: "12px",
                    padding: "24px",
                    color: "#0F1A3D",
                    display: "block",
                    transition: "box-shadow 0.3s ease",
                    cursor: "pointer",
                  }}
                  className="hover:shadow-2xl group"
                  data-testid={`hero-cta-${c.label.toLowerCase()}`}
                >
                  <div
                    className={`bg-gradient-to-br ${c.color}`}
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "8px",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <Icon style={{ width: "24px", height: "24px" }} strokeWidth={1.7} />
                  </div>
                  <div
                    style={{
                      fontSize: "10px",
                      textTransform: "uppercase",
                      letterSpacing: "0.2em",
                      fontWeight: 700,
                      color: "#E91E63",
                      marginBottom: "4px",
                    }}
                  >
                    {c.sub}
                  </div>
                  <div
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: "24px",
                      fontWeight: 800,
                      color: "#0F1A3D",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {c.label}
                    <ArrowRight style={{ width: "16px", height: "16px", color: "#1E3A8A" }} />
                  </div>
                </div>
              );
              return c.external ? (
                <a key={c.label} href={c.to}>{inner}</a>
              ) : (
                <Link key={c.label} to={c.to}>{inner}</Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== STATS ANIMÉES ===== */}
      <StatsBand />

      {/* ===== PROJETS PHARES (carrousel horizontal scrollable) ===== */}
      {projects.length > 0 && (
        <section className="bg-white py-20 lg:py-24" data-testid="featured-projects">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex items-end justify-between mb-10 lg:mb-14 flex-wrap gap-4">
              <div className="max-w-2xl">
                <div className="text-[12px] text-[#E91E63] font-bold tracking-[0.3em] mb-3">// PROJETS PHARES</div>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Découvrez nos<br />résidences emblématiques
                </h2>
              </div>
              <Link to={sitePath("/projets")} className="text-[13px] font-bold uppercase tracking-wider text-[#1E3A8A] hover:text-[#E91E63] inline-flex items-center gap-2 group">
                Tous les projets <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
              {projects.slice(0, 6).map((p, idx) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: Math.min(idx * 0.06, 0.4), ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={sitePath(`/projet/${p.slug || p.id}`)}
                    data-testid={`site-project-card-${p.id}`}
                    className="group block bg-white rounded-xl border border-[#E2E5F1] hover-lift overflow-hidden h-full"
                  >
                    <div className="overflow-hidden">
                      <div className="img-zoom-hover">
                        <SmartImage
                          src={p.cover_url ? `${BACKEND}${p.cover_url}` : null}
                          alt={p.nom}
                          aspectRatio="aspect-[4/3]"
                          fallbackLabel={p.type === "maison" ? "Lot maisons" : "Résidence"}
                        />
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#E91E63] font-bold">
                          {p.type === "maison" ? "Lot maisons" : "Résidence"}
                        </div>
                        {p.nb_disponibles > 0 && (
                          <span className="text-[10px] uppercase tracking-wider text-[#2D7A3F] font-bold px-2 py-0.5 rounded bg-[#E5F8E9]">
                            {p.nb_disponibles} dispo.
                          </span>
                        )}
                      </div>
                      <h3 className="font-display text-xl lg:text-2xl font-extrabold leading-[1.15] tracking-tight text-[#0F1A3D] group-hover:text-[#1E3A8A] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {p.nom}
                      </h3>
                      {p.slogan && <p className="text-sm text-[#4B5775] line-clamp-2 mt-2 leading-relaxed">{p.slogan}</p>}
                      <div className="flex items-center justify-between mt-5 pt-5 border-t border-[#F1F3FB]">
                        <div className="text-xs text-[#4B5775] flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-[#1E3A8A]" />
                          {p.quartier ? `${p.quartier}, ${p.ville}` : p.ville}
                        </div>
                        <div className="text-[12px] text-[#1E3A8A] font-bold uppercase tracking-wider inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                          Découvrir <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== POURQUOI TPCO ===== */}
      <WhyTpco />

      {/* ===== NOTRE MÉTHODE ===== */}
      <MethodTimeline />

      {/* ===== CALCULATEUR DE MENSUALITÉ ===== */}
      <MortgageCalculator />

      {/* ===== TÉMOIGNAGES ===== */}
      <Testimonials />

      {/* ===== NEWSLETTER / BROCHURE ===== */}
      <NewsletterCta />

      {/* ===== CTA FINAL ===== */}
      <section className="bg-[#0F1A3D] text-white py-20 lg:py-24">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <div className="text-[12px] text-[#E91E63] font-bold mb-3 tracking-[0.3em]">// À VOTRE SERVICE</div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Un projet ? <span className="text-[#E91E63]">Parlons-en.</span>
          </h2>
          <p className="text-white/70 text-base lg:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            TPCO Sénégal vous assiste, cerne vos besoins et vous propose une étude personnalisée gratuite.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to={sitePath("/contact")} className="inline-flex items-center gap-2 h-14 px-8 bg-[#E91E63] hover:bg-[#D81B60] text-white text-[13px] font-bold uppercase tracking-wider transition" data-testid="footer-cta-devis">
              Demander un devis <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={sitePath("/projets")} className="inline-flex items-center gap-2 h-14 px-8 border-2 border-white/30 hover:border-white text-white text-[13px] font-bold uppercase tracking-wider transition">
              Voir nos projets
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
